"use client";

import Image from "next/image";
import styles from "./gallery.module.css";

import {
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  X,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
} from "react";

type PublicItemGalleryProps = {
  images: string[];
  itemName: string;
  description?: string;
  previewOnly?: boolean;
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

export default function PublicItemGallery({
  images,
  itemName,
  description,
  previewOnly = false,
}: PublicItemGalleryProps) {
  const previewDialog = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] =
    useState(false);

  const [currentIndex, setCurrentIndex] =
    useState(0);

  const [zoom, setZoom] =
    useState(MIN_ZOOM);

  const touchStartX = useRef<number | null>(
    null,
  );

  const touchStartY = useRef<number | null>(
    null,
  );

  const pinchStartDistance =
    useRef<number | null>(null);

  const pinchStartZoom =
    useRef(MIN_ZOOM);

  const currentImage =
    images[currentIndex] ?? images[0];

  function resetZoom() {
    setZoom(MIN_ZOOM);
  }

  function openGallery(index: number) {
    setCurrentIndex(index);
    resetZoom();
    setIsOpen(true);
  }

  function closeGallery() {
    setIsOpen(false);
    resetZoom();
  }

  function showPrevious() {
    setCurrentIndex((current) =>
      current === 0
        ? images.length - 1
        : current - 1,
    );

    resetZoom();
  }

  function showNext() {
    setCurrentIndex((current) =>
      current === images.length - 1
        ? 0
        : current + 1,
    );

    resetZoom();
  }

  function zoomIn() {
    setZoom((current) =>
      Math.min(
        MAX_ZOOM,
        current + ZOOM_STEP,
      ),
    );
  }

  function zoomOut() {
    setZoom((current) =>
      Math.max(
        MIN_ZOOM,
        current - ZOOM_STEP,
      ),
    );
  }

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";
    const dialog = previewDialog.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (previewOnly) dialog?.showModal();

    function handleKeyDown(
      event: KeyboardEvent,
    ) {
      if (event.key === "Escape") {
        if (previewOnly) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        closeGallery();
        return;
      }

      if (previewOnly && event.key === "Tab" && dialog) {
        const controls = Array.from(dialog.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (first && last && (!dialog.contains(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last))) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
        return;
      }

      if (event.key === "ArrowLeft") {
        showPrevious();
        return;
      }

      if (event.key === "ArrowRight") {
        showNext();
        return;
      }

      if (
        event.key === "+" ||
        event.key === "="
      ) {
        zoomIn();
        return;
      }

      if (event.key === "-") {
        zoomOut();
      }
    }

    if (previewOnly) document.addEventListener("keydown", handleKeyDown, true);
    else window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow =
        previousOverflow;

      if (previewOnly) document.removeEventListener("keydown", handleKeyDown, true);
      else window.removeEventListener("keydown", handleKeyDown);
      if (previewOnly) {
        dialog?.close();
        if (previousFocus?.isConnected) previousFocus.focus({preventScroll: true});
      }
    };
  }, [isOpen, images.length, previewOnly]);

  function getTouchDistance(
    first: React.Touch,
    second: React.Touch,
  ) {
    return Math.hypot(
      second.clientX - first.clientX,
      second.clientY - first.clientY,
    );
  }

  function handleTouchStart(
    event: React.TouchEvent<HTMLDivElement>,
  ) {
    if (event.touches.length === 2) {
      pinchStartDistance.current =
        getTouchDistance(
          event.touches[0],
          event.touches[1],
        );

      pinchStartZoom.current = zoom;

      touchStartX.current = null;
      touchStartY.current = null;

      return;
    }

    if (
      event.touches.length === 1 &&
      zoom === MIN_ZOOM
    ) {
      touchStartX.current =
        event.touches[0].clientX;

      touchStartY.current =
        event.touches[0].clientY;
    }
  }

  function handleTouchMove(
    event: React.TouchEvent<HTMLDivElement>,
  ) {
    if (
      event.touches.length !== 2 ||
      pinchStartDistance.current === null
    ) {
      return;
    }

    const distance =
      getTouchDistance(
        event.touches[0],
        event.touches[1],
      );

    const ratio =
      distance /
      pinchStartDistance.current;

    const nextZoom =
      pinchStartZoom.current * ratio;

    setZoom(
      Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, nextZoom),
      ),
    );
  }

  function handleTouchEnd(
    event: React.TouchEvent<HTMLDivElement>,
  ) {
    if (event.touches.length < 2) {
      pinchStartDistance.current = null;
    }

    if (
      event.changedTouches.length !== 1 ||
      touchStartX.current === null ||
      touchStartY.current === null ||
      zoom !== MIN_ZOOM
    ) {
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }

    const touch =
      event.changedTouches[0];

    const deltaX =
      touch.clientX -
      touchStartX.current;

    const deltaY =
      touch.clientY -
      touchStartY.current;

    touchStartX.current = null;
    touchStartY.current = null;

    if (
      Math.abs(deltaX) < 50 ||
      Math.abs(deltaX) <=
        Math.abs(deltaY)
    ) {
      return;
    }

    if (deltaX < 0) {
      showNext();
      return;
    }

    showPrevious();
  }

  function handleDoubleClick() {
    setZoom((current) =>
      current > MIN_ZOOM
        ? MIN_ZOOM
        : 2,
    );
  }

  if (images.length === 0) {
    return null;
  }

  if (previewOnly) {
    return (
      <>
        <button type="button" aria-label={`Ampliar imagem de ${itemName}`} onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget.focus({preventScroll: true});
          openGallery(0);
        }} className={styles.previewButton}>
          <Image unoptimized src={images[0]} alt="" loading="lazy" decoding="async" width={48} height={48} className={styles.previewThumb} />
        </button>
        {isOpen && currentImage && (
          <dialog ref={previewDialog} aria-label={`Imagem de ${itemName}`} onCancel={event => {event.preventDefault(); closeGallery();}} onClick={event => {
            event.stopPropagation();
            if (event.target === event.currentTarget) closeGallery();
          }} className={styles.previewDialog}>
            <div className={styles.previewHeader}>
              <h2 className={styles.previewTitle}>{itemName}</h2>
              <button type="button" autoFocus aria-label="Fechar imagem" onClick={closeGallery} className={styles.previewClose}><X size={24} /></button>
            </div>
            <div className={styles.previewBody}>
              <Image unoptimized src={currentImage} alt={itemName} width={1200} height={1200} className={styles.previewImage} />
              {description && <p className={styles.previewDescription}>{description}</p>}
            </div>
          </dialog>
        )}
      </>
    );
  }

  return (
    <>
      <section className={styles.mosaic}>
        {images.length === 1 ? (
          <button
            type="button"
            onClick={() => openGallery(0)}
            aria-label="Abrir foto do item"
            className={styles.single}
          >
            <img
              src={images[0]}
              alt={itemName}
              className={styles.cover}
            />
          </button>
        ) : (
          <div className={styles.mosaicGrid}>
            <button
              type="button"
              onClick={() => openGallery(0)}
              aria-label="Abrir foto 1"
              className={styles.mainPhoto}
            >
              <img
                src={images[0]}
                alt={itemName}
                className={styles.cover}
              />
            </button>

            <div className={styles.sidePhotos}>
              {images
                .slice(1, 3)
                .map((image, index) => {
                  const imageIndex =
                    index + 1;

                  const showMore =
                    index === 1 &&
                    images.length > 3;

                  return (
                    <button
                      type="button"
                      key={image}
                      onClick={() =>
                        openGallery(
                          imageIndex,
                        )
                      }
                      aria-label={
                        showMore
                          ? `Abrir galeria com ${images.length} fotos`
                          : `Abrir foto ${imageIndex + 1}`
                      }
                      className={styles.sidePhoto}
                    >
                      <img
                        src={image}
                        alt={`${itemName} - foto ${imageIndex + 1}`}
                        className={styles.cover}
                      />

                      {showMore && (
                        <div className={styles.moreShade}>
                          <span className={styles.moreCount}>
                            +{" "}
                            {images.length - 3}{" "}
                            fotos
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </section>

      {isOpen && currentImage && (
        <div
          className={styles.overlay}
          role="dialog"
          aria-modal="true"
          aria-label={`Galeria de fotos de ${itemName}`}
        >
          <div className={styles.toolbar}>
            <div className={styles.minWidth}>
              <p className={styles.caption}>
                {itemName}
              </p>
            </div>

            <div className={styles.controls}>
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoom <= MIN_ZOOM}
                aria-label="Diminuir zoom"
                className={styles.control}
              >
                <Minus size={20} />
              </button>

              <span className={styles.zoomLabel}>
                {Math.round(zoom * 100)}%
              </span>

              <button
                type="button"
                onClick={zoomIn}
                disabled={zoom >= MAX_ZOOM}
                aria-label="Aumentar zoom"
                className={styles.control}
              >
                <Plus size={20} />
              </button>

              <button
                type="button"
                onClick={closeGallery}
                aria-label="Fechar galeria"
                className={styles.close}
              >
                <X size={22} />
              </button>
            </div>
          </div>

          <div
            className={styles.stage}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {images.length > 1 && (
              <button
                type="button"
                onClick={showPrevious}
                aria-label="Foto anterior"
                className={styles.previous}
              >
                <ChevronLeft size={28} />
              </button>
            )}

            <div
              className={styles.canvas}
              onDoubleClick={
                handleDoubleClick
              }
            >
              <img
                src={currentImage}
                alt={`${itemName} - foto ${currentIndex + 1}`}
                draggable={false}
                className={styles.fullImage}
                style={{
                  transform: `scale(${zoom})`,
                }}
              />
            </div>

            {images.length > 1 && (
              <button
                type="button"
                onClick={showNext}
                aria-label="Próxima foto"
                className={styles.next}
              >
                <ChevronRight size={28} />
              </button>
            )}
          </div>

          {images.length > 1 && (
            <div className={styles.strip}>
              <div className={styles.thumbnails}>
                {images.map(
                  (image, index) => (
                    <button
                      type="button"
                      key={image}
                      onClick={() => {
                        setCurrentIndex(
                          index,
                        );
                        resetZoom();
                      }}
                      aria-label={`Mostrar foto ${index + 1}`}
                      className={`${styles.thumbnail} ${index === currentIndex ? styles.activeThumbnail : styles.inactiveThumbnail}`}
                    >
                      <img
                        src={image}
                        alt=""
                        className={styles.thumbImage}
                      />
                    </button>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
